import { FC, useEffect } from 'react';
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { setCertStatus } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { userService } from 'src/services/User.service';
import type {
  Certification,
  CertificationProperty,
} from 'src/types/Certification';
import MemberCarousel, { ManagedMember } from '../Home/MemberCarousel';
import {
  certStatusState,
  getMemberSurvey,
} from '../Household/householdSurveyUtils';
import { certKindLabel } from '../utils/certTerminology';
import { certStatusActionNavigation } from './certStatusActions';
import CertStatusDetail from './CertStatusDetail';

export interface CertStatusOverviewItem {
  cert: Certification;
  certId: number;
  categoryName?: string;
  property?: CertificationProperty;
}

interface CertStatusOverviewModalProps {
  open: boolean;
  onClose: () => void;
  members: ManagedMember[];
  selectedUserId: number;
  onSelectMember: (userId: number) => void;
  membersWithActions: Set<number>;
  items: CertStatusOverviewItem[];
  actionsLoading: boolean;
}

const CertStatusOverviewModal: FC<CertStatusOverviewModalProps> = ({
  open,
  onClose,
  members,
  selectedUserId,
  onSelectMember,
  membersWithActions,
  items,
  actionsLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  const certStatus = useSelector(
    (state: RootState) => state.newRenterInterface.certStatus ?? {}
  );

  // Lazy-fetch any missing status details when the modal opens. `certStatus`
  // is intentionally NOT a dependency: the fetch itself dispatches
  // setCertStatus, which replaces the map reference, so depending on it would
  // re-run this effect after a partial result that leaves an id undefined.
  useEffect(() => {
    if (!open) {
      return;
    }
    const missing = items
      .filter((i) => certStatus[i.certId] === undefined)
      .map((i) => i.certId);
    if (missing.length === 0) {
      return;
    }
    let cancelled = false;
    userService
      .fetchCertStatus(missing)
      .then((data) => {
        if (!cancelled) {
          dispatch(setCertStatus(data ?? {}));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch cert status', err);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items, dispatch]);

  const header = (
    <Box sx={{ backgroundColor: 'primary.dark', px: 3, pt: 2.5, pb: 2 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: 'common.white', flex: 1 }}
        >
          My Pronto Status
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon sx={{ color: 'common.white', fontSize: 20 }} />
        </IconButton>
      </Stack>
      <Typography
        variant="body2"
        sx={{ color: 'common.white', opacity: 0.75, mt: 0.5 }}
      >
        Here's where everything stands
      </Typography>
    </Box>
  );

  const body = (
    <Stack gap={2} sx={{ p: 3, pb: 4 }}>
      {members.length > 1 ? (
        <MemberCarousel
          members={members}
          selectedUserId={selectedUserId}
          onSelect={onSelectMember}
          membersWithActions={membersWithActions}
          actionsLoading={actionsLoading}
        />
      ) : null}

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active certifications.
        </Typography>
      ) : (
        items.map(({ cert, certId, categoryName, property }) => {
          const memberCertMemberId = cert.cert_household?.cert_members?.find(
            (m) => m.user_id === selectedUserId
          )?.cert_member_id;
          const state = certStatusState(
            certStatus[certId],
            selectedUserId,
            memberCertMemberId
          );

          return (
            <Box
              key={certId}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '12px',
                boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
                p: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
                    {categoryName || cert.category?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {property?.name ?? cert.property?.name}
                    {cert.unit?.name ? ` · Unit ${cert.unit.name}` : ''}
                  </Typography>
                </Box>
                {state === 'decision' ? (
                  <Chip
                    icon={
                      <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                    }
                    label="Decision issued"
                    size="small"
                    sx={{
                      backgroundColor: 'info.lighter',
                      color: 'info.dark',
                      fontWeight: 500,
                      fontSize: 12,
                      height: 26,
                      '& .MuiChip-icon': { color: 'info.dark' },
                    }}
                  />
                ) : state === 'action' ? (
                  <Chip
                    icon={<BoltIcon sx={{ fontSize: '14px !important' }} />}
                    label="Needs action"
                    size="small"
                    sx={{
                      backgroundColor: 'error.lighter',
                      color: 'error.dark',
                      fontWeight: 500,
                      fontSize: 12,
                      height: 26,
                      '& .MuiChip-icon': { color: 'error.dark' },
                    }}
                  />
                ) : (
                  <Chip
                    icon={
                      <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                    }
                    label="In review"
                    size="small"
                    sx={{
                      backgroundColor: 'success.lighter',
                      color: 'success.dark',
                      fontWeight: 500,
                      fontSize: 12,
                      height: 26,
                      '& .MuiChip-icon': { color: 'success.dark' },
                    }}
                  />
                )}
              </Stack>
              <CertStatusDetail
                detail={certStatus[certId] ?? null}
                selectedUserId={selectedUserId}
                selectedCertMemberId={memberCertMemberId}
                loading={!certStatus[certId]}
                completeStateCertKind={certKindLabel(cert)}
                onAction={(id) => {
                  const surveyId =
                    memberCertMemberId != null
                      ? getMemberSurvey(cert, memberCertMemberId)
                      : null;
                  const { path, state: navState } = certStatusActionNavigation(
                    id,
                    surveyId,
                    selectedUserId !== user?.id ? selectedUserId : undefined
                  );
                  if (path == null) {
                    return;
                  }
                  onClose();
                  if (navState) {
                    navigate(path, { state: navState });
                  } else {
                    navigate(path);
                  }
                }}
              />
            </Box>
          );
        })
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            margin: isMobile ? '0 !important' : 4,
            height: isMobile ? '100%' : 'auto',
            maxHeight: isMobile ? '100%' : '80dvh',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : theme.palette.background.neutral,
          },
        },
      }}
    >
      {header}
      {body}
    </Dialog>
  );
};

export default CertStatusOverviewModal;
